import Table from "@/components/Table/table";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import TitleComplement from "@/features/debtor/debtorTab/components/titleComplement";
import { useGetDebtorListQuery } from "@/services/debtors/debtorsReducers";
import { useColumnDefs } from "@/features/debtor/debtorTab/Constant/debtorHeaderConst";
import { useParams } from "react-router-dom";
import { useCustomCellRenderers } from "@/features/debtor/debtorTab/Constant/debtorCustomCellConst";
import {
  ActiveClaimModel,
  NoClaimModel,
} from "@/features/debtor/debtorTab/modelBox";
import { useCase } from "@/features/case/use-case";
import CustomPagination from "@/components/Pagination/CustomPagination";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
} from "@/components/Table/CustomPaginationConstants";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";
interface BannerData {
  debtorId: string;
  caseId: string;
  name: string;
  description: string;
  createdBy: string;
  updatedBy: string;
  isActive: boolean;
}

interface DebtorTabContentProps {
  bannerData: BannerData[];
  isSuccess?: boolean;
}

const DebtorTabContent = ({ bannerData }: DebtorTabContentProps) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [activeClaim, setActiveClaim] = useState(undefined);
  const [debtorId, setDebtorId] = useState();
  const { caseId } = useParams<{ caseId: string }>();
  const [debtorName, setDebtorName] = useState<string>("");
  const caseData = useCase(caseId);
  let tableLoadingData = customTableLoadingData;
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  const retainState = useSelector((e: any) => e.retainState.debtorListTabState);
  const dispatch = useDispatch();
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      CaseID: caseId || caseData.caseId,
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize || paginationPageSize,
      SearchText: retainState?.SearchText || "",
    };
    if (retainState?.SortOrder && retainState?.SortColumn) {
      value.SortOrder = retainState?.SortOrder;
      value.SortColumn = retainState?.SortColumn;
    }
    return value;
  }, [caseData?.caseId, caseId, paginationPageSize, retainState]);
  const paginationObj = getPaginationObj;

  const permissions = useSelector((state: any) => state.permissions);
  const { Debtor } = permissions;

  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "debtorListTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
  }
  const { data, error, isLoading, isFetching, refetch,status } =
    useGetDebtorListQuery(paginationObj);
  const [searchQuery, setSearchQuery] = useState(retainState.SearchText || "");
  let columnDefs = useColumnDefs(caseData);
  let resetSortStatus = useCallback((defaultSort:string) => {
    let json: any = { ...retainState.sortedColumnTypes };
    for (const key in json) {
      json[key] = { sort: null };
    }
    json[defaultSort]={sort: "asc"};
    return json;
  }, [retainState.sortedColumnTypes]);
  const handleSearch = useCallback(
    (searchValue: string) => {
      if (searchValue.trim() === "") {
        dispatch(
          setComponentState1({
            entityName: "debtorListTabState",
            entityState: {
              PageNumber: 1,
              PageSize: paginationPageSize,
              SearchText: "",
              sortedColumnTypes: { ...resetSortStatus("nameType") },
              SortOrder: "asc",
              SortColumn: "Name",
            },
          })
        );
      } else {
        dispatch(
          setComponentState1({
            entityName: "debtorListTabState",
            entityState: {
              PageNumber: 1,
              PageSize: paginationPageSize,
              SearchText: searchValue,
              sortedColumnTypes: { ...resetSortStatus("nameType") },
              SortOrder: "asc",
              SortColumn: "Name",
            },
          })
        );
      }
      setSearchQuery(searchValue);
    },
    [paginationPageSize, resetSortStatus, dispatch]
  );

  const handleQuickSearchClear = useCallback(() => {
    setSearchQuery("");

    dispatch(
      setComponentState1({
        entityName: "debtorListTabState",
        entityState: {
          PageNumber: 1,
          PageSize: paginationPageSize,
          SearchText: "",
          sortedColumnTypes: { ...resetSortStatus("nameType") },
          SortOrder: "asc",
          SortColumn: "Name",
        },
      })
    );
  }, [paginationPageSize, resetSortStatus, dispatch]);

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderers = useCustomCellRenderers(
    showModal,
    setShowModal,
    setActiveClaim,
    setDebtorId,
    Debtor,
    setDebtorName
  );

  const headerData = {
    title: "pages.debtors.debtorsTableTile",
    description: "pages.debtors.debtorsDescription",
  };

  if (isLoading) {
    return <div> {t("global.globalNotifications.loadingMessage")} </div>;
  }
  const tableData = data?.results ? data.results : [];
  return (
    <div className={"ccwp_claim_claimlisttable"}>
      {activeClaim === 0 ? (
        <NoClaimModel
          showModal={showModal}
          setShowModal={setShowModal}
          activeClaim={activeClaim}
          setActiveClaim={setActiveClaim}
          debtorId={debtorId}
          setDebtorId={setDebtorId}
          refetch={refetch}
          setDebtorName={setDebtorName}
          debtorName={debtorName}
        />
      ) : (
        <ActiveClaimModel
          showModal={showModal}
          setShowModal={setShowModal}
          activeClaim={activeClaim}
          setActiveClaim={setActiveClaim}
          debtorName={debtorName}
          setDebtorName={setDebtorName}
        />
      )}
      <>
        <Table
          columnDefs={columnDefs}
          columnTypes={{ ...retainState.sortedColumnTypes }}
          handlePaginationData={handlePaginationData}
          paginationObj={paginationObj}
          cellRenderers={isFetching ? [] : tableData}
          TitleComplement={() => (
            <TitleComplement
              onSearch={handleSearch}
              handleQuickSearchClear={handleQuickSearchClear}
              searchQuery={searchQuery}
              Debtor={Debtor}
            />
          )}
          title={headerData.title}
          description={headerData.description}
          caseRenderers={renderers}
          // pagination={true}
          overlayNoRowsTemplate={
            isFetching
              ? t(tableLoadingData)
              : error
              ? t("global.globalNotifications.wentWrong")
              : t("global.globalNotifications.noRecords")
          }
        />
        <CustomPagination
          apiData={data}
          apiStatus={status}
          currentpage={paginationObj.PageNumber}
          pageSize={paginationObj.PageSize}
          disableNext={!data?.hasMoreRecords}
          onChangePageSize={(page: number) => {
            dispatch(
              setComponentState1({
                entityName: "debtorListTabState",
                entityState: {
                  PageSize: page,
                  PageNumber: 1,
                },
              })
            );
          }}
          onChangeNextPage={() => {
            dispatch(
              setComponentState1({
                entityName: "debtorListTabState",
                entityState: {
                  PageNumber: paginationObj.PageNumber + 1,
                },
              })
            );
          }}
          onChangePreviousPage={() => {
            dispatch(
              setComponentState1({
                entityName: "debtorListTabState",
                entityState: {
                  PageNumber: paginationObj.PageNumber - 1,
                },
              })
            );
          }}
          pageSizeSelector={paginationPageSizeSelector}
        />
      </>
    </div>
  );
};

export default DebtorTabContent;
